/**
 * Square Web SDK Configuration
 * 
 * This module provides centralized configuration management for Square Web SDK
 * integration. It handles environment-specific settings, validation, and
 * provides utilities for Square API interactions.
 * 
 * @module squareConfig
 */

/**
 * Square Environment Configuration
 * Validates and provides Square SDK configuration based on environment variables
 */
class SquareConfig {
    constructor() {
        this.config = this.loadConfiguration();
        this.validateConfiguration();
    }

    /**
     * Loads configuration from environment variables
     * @returns {Object} Configuration object
     */
    loadConfiguration() {
        const config = {
            // Square SDK Configuration
            environment: process.env.REACT_APP_SQUARE_ENVIRONMENT || 'sandbox',
            applicationId: process.env.REACT_APP_SQUARE_APPLICATION_ID,
            locationId: process.env.REACT_APP_SQUARE_LOCATION_ID,
            sdkUrl: process.env.REACT_APP_SQUARE_SDK_URL || 'https://web.squarecdn.com/v1/square.js',

            // Backend Configuration
            backendUrl: process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000',

            // Application Configuration
            cookieName: process.env.REACT_APP_COOKIE_NAME || 'prysm-registration'
        };

        // Log configuration status (without sensitive data)
        console.log('[SquareConfig] Configuration loaded:', {
            environment: config.environment,
            hasApplicationId: !!config.applicationId,
            hasLocationId: !!config.locationId,
            sdkUrl: config.sdkUrl,
            backendUrl: config.backendUrl
        });

        return config;
    }

    /**
     * Validates the loaded configuration
     * @throws {Error} If configuration is invalid
     */
    validateConfiguration() {
        const errors = [];

        // Validate environment
        if (!this.config.environment || !['sandbox', 'production'].includes(this.config.environment)) {
            errors.push('REACT_APP_SQUARE_ENVIRONMENT must be either "sandbox" or "production"');
        }

        // Validate required Square credentials
        if (!this.config.applicationId) {
            errors.push('REACT_APP_SQUARE_APPLICATION_ID is required');
        }

        if (!this.config.locationId) {
            errors.push('REACT_APP_SQUARE_LOCATION_ID is required');
        }

        // Validate SDK URL format
        if (!this.config.sdkUrl || !this.config.sdkUrl.startsWith('https://')) {
            errors.push('REACT_APP_SQUARE_SDK_URL must be a valid HTTPS URL');
        }

        // Validate backend URL format
        if (!this.config.backendUrl || (!this.config.backendUrl.startsWith('http://') && !this.config.backendUrl.startsWith('https://'))) {
            errors.push('REACT_APP_BACKEND_URL must be a valid HTTP/HTTPS URL');
        }

        if (errors.length > 0) {
            const errorMessage = `Square Configuration Validation Failed:\n${errors.join('\n')}`;
            console.error('[SquareConfig]', errorMessage);
            throw new Error(errorMessage);
        }

        console.log('[SquareConfig] Configuration validation passed');
    }

    /**
     * Gets the complete configuration object
     * @returns {Object} Configuration object
     */
    getConfig() {
        return { ...this.config };
    }

    /**
     * Gets Square-specific configuration
     * @returns {Object} Square SDK configuration
     */
    getSquareConfig() {
        return {
            environment: this.config.environment,
            applicationId: this.config.applicationId,
            locationId: this.config.locationId,
            sdkUrl: this.config.sdkUrl
        };
    }

    /**
     * Gets backend API configuration
     * @returns {Object} Backend configuration
     */
    getBackendConfig() {
        return {
            backendUrl: this.config.backendUrl,
            cookieName: this.config.cookieName
        };
    }

    /**
     * Checks if the current environment is production
     * @returns {boolean} True if production environment
     */
    isProduction() {
        return this.config.environment === 'production';
    }

    /**
     * Checks if the current environment is sandbox
     * @returns {boolean} True if sandbox environment
     */
    isSandbox() {
        return this.config.environment === 'sandbox';
    }

    /**
     * Gets environment-specific API endpoints
     * @returns {Object} API endpoints configuration
     */
    getApiEndpoints() {
        const baseUrl = this.config.backendUrl;

        return {
            processPayment: `${baseUrl}/api/payments/process`,
            createCustomer: `${baseUrl}/api/payments/customers`,
            getPaymentMethods: `${baseUrl}/api/payments/methods`,
            refundPayment: `${baseUrl}/api/payments/refund`
        };
    }

    /**
     * Gets Square Web SDK initialization parameters
     * @returns {Object} Square SDK initialization parameters
     */
    getSquareInitParams() {
        return {
            applicationId: this.config.applicationId,
            locationId: this.config.locationId
        };
    }

    /**
     * Gets payment form styling configuration
     * Only includes Square Web SDK approved CSS properties
     * @returns {Object} CSS styling for Square payment forms
     */
    getPaymentFormStyles() {
        return {
            '.input-container': {
                borderColor: '#D1D5DB'
            },
            '.input-container.is-focus': {
                borderColor: '#8B5CF6'
            },
            '.input-container.is-error': {
                borderColor: '#EF4444'
            }
        };
    }
}

// Create and export singleton instance
const squareConfig = new SquareConfig();

export default squareConfig;

// Export individual configuration getters for convenience
export const {
    getConfig,
    getSquareConfig,
    getBackendConfig,
    isProduction,
    isSandbox,
    getApiEndpoints,
    getSquareInitParams,
    getPaymentFormStyles
} = squareConfig;
